import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { ItemCtaSimple } from '@marty-js/design/src/molecules/item-cta-simple';
import { HeadingTwo } from '@marty-js/design/src/atoms/h2';
import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import { useTranslation } from '@marty-js/design/src/utils/translation';
import { useTrackEvent } from '@marty-js/api-sdk/services/matomo';
import { Slider } from '@marty-js/design/src/molecules/slider';
import { SimpleSoftwareCard } from '@marty-js/design/src/molecules/cards/software/simple';
import { Stars } from '@marty-js/design/src/molecules/stars';
import { ButtonDropdown } from '@marty-js/design/src/molecules/download/button-dropdown';
import { HeadingOne } from '@marty-js/design/src/atoms/h1';
import { Paragraph } from '@marty-js/design/src/atoms/paragraph';
import { Modal } from '@marty-js/design/src/molecules/download/modal';
import { useThemeSwitcher } from '@marty-js/design/src/utils/theme-switcher';
import { OtherFiles } from '@marty-js/design/src/molecules/download/other-files';
import { StarsRate } from '@marty-js/design/src/molecules/starsRate';
import { DEVICE_SIZE, mq } from '@marty-js/design/src/utils/mq';
import { useRateItem } from '@marty-js/api-sdk/services/software';
import type { LayoutItemProps } from '../../types';
import { DownloadData, File, TrackEventCustomVariable, TrackViewDimension } from './types';
import { Breadcrumb } from '../../../../atoms/breadcrumb';
import {
  Container,
  MainContent,
  SideContent,
  SideContentHalf,
  SideContentSticky,
  SideContentSpacer,
  FullContentRelative,
  Background,
} from '../../../../atoms/grid-container';
import { Ad } from '../../../../ads/ad';
import { useMetaSeoContext } from '../../../../utils/metaSeoContext';
import { DataType, SponsoFactory, TemplateType } from '../../../../utils/sponsoFactory';

import type { DownloadModalData } from '../../../../types';
import { useAddServerSideZones, useAdContext } from '../../../../ads/AdContext';
import { ComponentProps, CustomeGenericItem } from '../../../../types';
import { DateDisplay } from '../../../../atoms/date/date';
import { Image } from '../../../../atoms/image/image';
import { SoftwareSpecsFile, SoftwareSpecs } from '../../../../atoms/software-specs';

import DownloadSheet from './downloadSheet';
import PreLaunch from './preLaunch';
import { SoftwareScreenshots } from '../../../../atoms/software-screenshots';
import { ViewerProvider } from '../../../../article-processor/viewer-context';
import { useSetMaxFirstImages } from '../../../../utils/imageContext';
import { useUserAgentOs } from '../../../../utils/deviceHook';
import { DataZone } from '../../../../ads/types';

const StartDownload = dynamic(() => import('./startDownload'));
const Disk = dynamic(() => import('@marty-js/design/src/icons/disk'));

const thumbnailsOs: { [key: string]: any } = {
  Windows: dynamic(() => import('@marty-js/design/src/icons/os/windows')),
  Linux: dynamic(() => import('@marty-js/design/src/icons/os/linux')),
  macOS: dynamic(() => import('@marty-js/design/src/icons/os/macos')),
  iOS: dynamic(() => import('@marty-js/design/src/icons/os/ios')),
  Android: dynamic(() => import('@marty-js/design/src/icons/os/android')),
  Web: dynamic(() => import('@marty-js/design/src/icons/os/web')),
};

const SpacedContainer = styled(Container)`
  margin: 15px 0 0;
`;

const ListItemRow = styled.ul`
  display: flex;
  flex-direction: column;
`;

const DownloadHeader = styled.div`
  display: flex;
`;

const DownloadHeaderLeft = styled.div`
  display: none;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: block;
    `,
  )}
`;

const DownloadHeaderImage = styled.div`
  border: solid 1px #dddddd;
  border-radius: 10px;
  box-sizing: content-box;
  height: 45px;
  min-height: 45px;
  min-width: 45px;
  padding: 8px;
  width: 45px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      height: 94px;
      min-height: 94px;
      min-width: 94px;
      padding: 28px;
      width: 94px;

      &.mod-mobile {
        display: none;
      }
    `,
  )}
`;

const DownloadHeaderUpdate = styled.p`
  color: var(--color-alt-1);
  font-size: 12px;
  line-height: 24px;
  margin: 0 0 5px;
  text-align: center;
`;

const MobileDownloadHeaderUpdate = styled.p`
  color: var(--color-alt-1);
  font-size: 12px;
  line-height: 24px;
  margin: 0 0 5px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

const DownloadHeaderRight = styled.div`
  margin: 0 0 18px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 0 60px 18px 30px;
      width: 100%;
    `,
  )}
`;

const DownloadHeaderHeading = styled.div`
  align-items: center;
  display: flex;
  gap: 14px;
  margin: 0 0 10px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: initial;
      gap: initial;
    `,
  )}
`;

const DownloadHeaderStats = styled.div`
  align-items: center;
  color: var(--text-color-alt);
  display: flex;
  justify-content: space-around;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      justify-content: space-between;
    `,
  )}
`;

const DownloadHeaderStars = styled.div`
  padding: 0 19px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      padding: 0;
      flex-direction: row;
      text-align: initial;
    `,
  )}
`;

const DownloadHeaderRating = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const RatingValue = styled.span`
  margin-left: 12px;
  font-weight: bold;
`;

const RatingValueComp = styled.span`
  font-weight: normal;
`;

const DownloadHeaderHr = styled.div`
  height: 50px;
  border-left: solid 1px var(--color-alt-3);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

const DownloadHeaderStatsMark = styled.div`
  line-height: 24px;
  margin: 0 0 0 6px;
  font-size: 13px;
  text-align: center;
  display: flex;

  flex-direction: column;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: row;
    `,
  )}
`;

const DownloadAdvise = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  align-items: center;
  color: var(--text-color-alt);
  justify-content: center;
  margin: 17px 0;
  user-select: none;
  height: 25px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: row;
      display: flex;
      gap: 18px;
      margin: 26px 0;
    `,
  )}
`;

const DownloadAdviseLabel = (props: React.HTMLAttributes<HTMLSpanElement>) => <span {...props} />;

const DownloadAdviseRating = styled.div`
  cursor: pointer;
  display: flex;
`;

const DownloadAdviseRatingStar = styled.button``;

const DownloadRatingMessage = styled.p`
  background: var(--theme-background-contrast-background-color);
  color: var(--text-color-alt);
  text-align: center;
  margin: 0 0 40px;
  padding: 8px;
`;

const DownloadRatingMessageValidate = styled.p`
  background: var(--theme-background-contrast-background-color);
  color: var(--text-color-alt);
  text-align: center;
  cursor: pointer;
  margin: 0 0 20px;
  padding: 8px;
`;

const FileOption = styled.div`
  align-items: center;
  display: flex;
  gap: 15px;
`;

const FileOptionSpan = styled.span`
  display: flex;
  flex-direction: column;
  font-size: 13px;
  line-height: 17px;

  strong {
    font-size: 18px;
    line-height: 24px;
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: initial;
      font-size: 24px;
      line-height: 28px;
      height: 22px;

      strong {
        font-size: 24px;
        line-height: 28px;
      }
    `,
  )}
`;

const AlsoAvailable = styled.div`
  margin: 45px 0;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 60px 0;
    `,
  )}
`;

const SideContentTopDownloadTitle = styled.span`
  display: block;
  font-family: var(--theme-typography-primary-font);
  font-size: 24px;
  font-weight: bold;
  line-height: 1.38;
  margin: var(--spacer-s) 0;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 26px;
    `,
  )}
`;

const RelativeTitle = styled.span`
  display: block;
  font-family: var(--theme-typography-primary-font);
  font-size: 22px;
  font-weight: bold;
  line-height: 1.38;
  margin: var(--spacer-s) 0;

  &.mod-fat {
    color: var(--theme-color-medium);
    font-size: 30px;
    font-weight: 900;

    ${mq.gte(
      DEVICE_SIZE.LARGE,
      css`
        font-size: 48px;
      `,
    )}
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 26px;
    `,
  )}
`;

const DownloadItem = ({ item, data, breadcrumb, set0verrideAdUnit }: LayoutItemProps<DownloadData>) => {
  const modalZone = useMemo(() => ({ zoneSlug: 'modal-dl', keywords: '' }), []);
  const modalData = useAddServerSideZones(modalZone);
  const [loadModalData, setLoadModalData] = useState<boolean>(false);

  const t = useTranslation();

  useSetMaxFirstImages(1);
  const [currentFile, setCurrentFile] = useState<File>(null);
  const [trigger, setTrigger] = useState<number>(0);
  const [isModal, setIsModal] = useState<boolean>(false);
  const adContext = useAdContext();
  const [trackEvent] = useTrackEvent();
  const metaSeoContext = useMetaSeoContext();
  const userAgentOs = useUserAgentOs();

  const [modalProps, setModalProps] = useState(data.windowsModal?.data?.data?.data);
  const [campaignName, setCampaignName] = useState(null);
  const isModalEnabled = !item.tagSlugs?.includes('no-modale');
  const isSponsoredItem = item.tagSlugs?.includes('sponsored');

  useEffect(() => {
    if (!loadModalData && modalData?.component?.data) {
      const dataModal: DownloadModalData = modalData.component?.data?.data?.data;
      if (dataModal) {
        setModalProps(dataModal);
        setCampaignName(modalData.component.name);
        setLoadModalData(true);
      }
    }
  }, [modalData, modalProps, setModalProps, setCampaignName, loadModalData, setLoadModalData]);

  const [startDownloadFile, getStartDownloadFile] = useState<string>(null);
  const [isPreLaunched, setIsPreLaunched] = useState<boolean>(false);

  const router = useRouter();
  const [fileOsDefault, setFileOsDefault] = useState<File>(data.fileList?.files[0]);

  const legacy = item.tagSlugs.includes('legacy');

  const isDev = process.env.NEXT_PUBLIC_APP_DEV;
  const zoneSponso = useMemo(() => ({ zoneSlug: `sponso-sidebar${isDev ? '-dev' : ''}`, keywords: '' }), [isDev]);
  const dataZoneSponso = useAddServerSideZones(zoneSponso);

  if (!metaSeoContext.title) {
    const fileList = data.fileList?.files || [];

    const uniqueOS = new Set<string>();

    fileList.forEach((file) => {
      if (file.os) {
        if (file.os === 'macOS') {
          uniqueOS.add('Mac');
        } else {
          uniqueOS.add(file.os);
        }
      }
    });

    const osList = Array.from(uniqueOS);

    metaSeoContext.title = t('sdk.template.download.metaTitle', { title: item.title, os: osList.join(', ') });
  }

  const handlerStartDownload = useCallback(
    (fileId: string) => {
      setIsPreLaunched(false);
      delete router.query.prelaunched;
      getStartDownloadFile(fileId);
    },
    [router.query.prelaunched],
  );

  const handlerRedirectLink = useCallback(
    (fileId: string, osSelected: string) => {
      const userToken = localStorage.getItem('actionToken');
      const trackEventCustomVariable: TrackEventCustomVariable[] = [{ id: 1, name: 'OS', value: osSelected }];
      trackEvent({
        variables: {
          userToken,
          eventCategory: 'SoftwarePage',
          eventName: 'download',
          actionName: 'download',
          eventValue: item.title,
          customDimensions: [],
          customVariables: trackEventCustomVariable,
          url: metaSeoContext.url,
        },
      });

      setIsPreLaunched(false);
      delete router.query.prelaunched;
    },
    [item.title, metaSeoContext.url, trackEvent, router.query.prelaunched],
  );

  const handlerPreLaunchedDownload = useCallback(
    (fileId: string, osSelected: string) => {
      const userToken = localStorage.getItem('actionToken');
      const trackEventCustomVariable: TrackEventCustomVariable[] = [{ id: 1, name: 'OS', value: osSelected }];
      trackEvent({
        variables: {
          userToken,
          eventCategory: 'SoftwarePage',
          eventName: 'pre_download',
          actionName: 'pre_download',
          eventValue: item.title,
          customDimensions: [],
          customVariables: trackEventCustomVariable,
          url: metaSeoContext.url,
        },
      });
      getStartDownloadFile(fileId);
      setIsPreLaunched(true);
      window.scrollTo(0, 0);
    },
    [item.title, metaSeoContext.url, trackEvent],
  );

  const triggerModal = useCallback(
    (f: File) => {
      const userToken = localStorage.getItem('actionToken');
      // No Modal direct link
      if (isSponsoredItem) {
        window.location.href = f.url;
        const trackEventCustomVariable: TrackEventCustomVariable[] = [{ id: 1, name: 'OS', value: f.os }];
        trackEvent({
          variables: {
            userToken,
            eventCategory: 'SoftwarePage',
            eventName: 'download',
            actionName: 'download',
            eventValue: item.title,
            customDimensions: [],
            customVariables: trackEventCustomVariable,
            url: metaSeoContext.url,
          },
        });

        // Model if not ios or android and tag not include "no-modale"
      } else if (modalProps && isModal && userAgentOs !== 'iOS' && userAgentOs !== 'Android' && isModalEnabled) {
        setCurrentFile(f);
        setTrigger(trigger + 1);
        modalData?.impressionCount();
        const trackViewDimension: TrackViewDimension[] = [{ id: 'dimension10', value: campaignName }];
        const trackEventCustomVariable: TrackEventCustomVariable[] = [
          { id: 10, name: 'campaignDL', value: campaignName },
        ];
        modalData?.impressionCount();
        trackEvent({
          variables: {
            userToken,
            eventCategory: 'SoftwarePage',
            eventName: 'display_modal',
            actionName: 'display_modal',
            eventValue: item.title,
            customDimensions: trackViewDimension,
            customVariables: trackEventCustomVariable,
            url: metaSeoContext.url,
          },
        });
      } else {
        setCurrentFile(f);
        handlerPreLaunchedDownload(f.id, f.os);
      }
      trackEvent({
        variables: {
          userToken,
          eventCategory: 'SoftwarePage',
          eventName: 'click_cta_dl',
          actionName: 'click_cta_dl',
          eventValue: JSON.stringify({ product: item.title, price: null, marchand: null, position: null }),
          customDimensions: [],
          customVariables: [],
          url: metaSeoContext.url,
        },
      });
    },
    [
      modalProps,
      isModal,
      userAgentOs,
      trigger,
      campaignName,
      trackEvent,
      item.title,
      metaSeoContext.url,
      handlerPreLaunchedDownload,
      isModalEnabled,
      isSponsoredItem,
      modalData,
    ],
  );

  const clickTopApp = (title: string) => {
    const userToken = localStorage.getItem('actionToken');

    trackEvent({
      variables: {
        userToken,
        eventCategory: 'BlocSidebar',
        eventName: 'topApp',
        actionName: 'topApp',
        eventValue: title,
        customDimensions: null,
        customVariables: null,
        url: metaSeoContext.url,
      },
    });
  };

  const allFiles = useMemo(
    () =>
      data.fileList?.files.map((curFile): SoftwareSpecsFile => {
        const newFile = JSON.parse(JSON.stringify(curFile));
        const curVariant = item.variants?.find((curVar) => curVar.id === curFile.variantId);
        if (curVariant) {
          newFile.dataSheet = curVariant.dataSheet;
        }

        return newFile;
      }),
    [data.fileList, item.variants],
  );

  const getTitle = useCallback(
    (f: File): any => {
      switch (f.os) {
        case 'Web':
          return <strong>{t('sdk.template.download.openWeb')}</strong>;
        default:
          return (
            <>
              <strong>{t('sdk.template.download.download')}</strong> {t('sdk.template.download.for')} {f.os}
            </>
          );
      }
    },
    [t],
  );

  const getFileTitle = useCallback(
    (f: File, fill: string) => {
      const Icon = thumbnailsOs[f.os];

      return (
        <FileOption>
          {Icon ? <Icon width={24} height={24} color={fill} /> : ''}
          <FileOptionSpan>
            {f.os === 'Web' ? (
              <strong>{t('sdk.template.download.openWeb')}</strong>
            ) : (
              <>
                <strong>{t('sdk.template.download.download')}</strong> {t('sdk.template.download.for')} {f.os}
              </>
            )}
          </FileOptionSpan>
        </FileOption>
      );
    },
    [t],
  );

  const [rateSoftware, message] = useRateItem();
  const [rating, setRating] = useState<number>(0);
  const [validateRating, setValidateRating] = useState<boolean>(false);

  const handleLike = () => {
    setValidateRating(false);
    const token = window.localStorage.getItem('actionToken');
    rateSoftware({ variables: { rate: rating, sourceId: item.id, token, type: 'item' } });
  };

  const ratingHover = (event: any, index: number) => {
    const boudingClientRect = event.target.getBoundingClientRect();
    const isHalf = event.clientX - event.target.getBoundingClientRect().left - boudingClientRect.width / 2 < 0;
    const newValue = isHalf ? index - 0.5 : index;

    setRating(newValue);

    return newValue;
  };

  const getLink = useCallback(
    (f: File, fill: string) => {
      return {
        ...f,
        title: getFileTitle(f, fill),
        onClick: isModal ? () => triggerModal(f) : null,
        a: isModal
          ? null
          : {
              href: `${item.url}?file=${f.id}&prelaunched=true`,
              rel: 'nofollow',
            },
      };
    },
    [triggerModal, getFileTitle, isModal, item.url],
  );

  const { currentTheme } = useThemeSwitcher();

  const links = useMemo(() => {
    return [
      getLink(fileOsDefault, 'white'),
      ...data.fileList?.files
        ?.filter((f) => f.os !== fileOsDefault.os)
        .map((f) => getLink(f, currentTheme.colors.foreground)),
    ];
  }, [currentTheme, fileOsDefault, getLink, data]);

  useEffect(() => {
    const fileOs = data.fileList?.files?.find((file) => file.os === userAgentOs);

    if (fileOs) {
      setFileOsDefault(fileOs);
    } else {
      const fileOsWeb = data.fileList?.files?.find((file) => file.os === 'Web');
      if (fileOsWeb) {
        setFileOsDefault(fileOsWeb);
      }
    }
  }, [data, userAgentOs]);

  const mainContent = useMemo(() => {
    const downloadInProgress = isPreLaunched || router.query?.prelaunched || router.query?.file || startDownloadFile;

    if (downloadInProgress) {
      const validFiles = data.fileList.files.filter((file) =>
        startDownloadFile ? file.id === startDownloadFile : file.id === router.query.file,
      );
      if (validFiles.length) {
        return isPreLaunched || router.query?.prelaunched ? (
          <PreLaunch
            item={item}
            data={data}
            breadcrumb={breadcrumb}
            set0verrideAdUnit={set0verrideAdUnit}
            handlerStartDownload={handlerStartDownload}
            handlerRedirectLink={handlerRedirectLink}
            file={validFiles[0]}
            userAgentOs={userAgentOs}
            thumbnailsOs={thumbnailsOs}
          />
        ) : (
          <StartDownload
            item={item}
            data={data}
            breadcrumb={breadcrumb}
            handlerRedirectLink={handlerRedirectLink}
            set0verrideAdUnit={set0verrideAdUnit}
            file={validFiles[0]}
            userAgentOs={userAgentOs}
            thumbnailOs={thumbnailsOs}
          />
        );
      }
    }

    return (
      <DownloadSheet
        item={item}
        data={data}
        breadcrumb={breadcrumb}
        set0verrideAdUnit={set0verrideAdUnit}
        file={fileOsDefault}
        userAgentOs={userAgentOs}
        legacy={legacy}
        links={links}
        setIsModal={setIsModal}
        campaignName={campaignName}
      />
    );
  }, [
    item,
    data,
    breadcrumb,
    set0verrideAdUnit,
    userAgentOs,
    legacy,
    fileOsDefault,
    router.query,
    startDownloadFile,
    handlerRedirectLink,
    handlerStartDownload,
    campaignName,
    links,
    setIsModal,
    isPreLaunched,
  ]);

  const alternateSoftwares = useMemo(() => {
    const list = data.softwareSimilar?.genericItemList;

    if (!list) {
      return null;
    }

    return (
      list.map((genericItem: any) => {
        const itemLink = {
          href: genericItem.url,
        };

        return (
          <SimpleSoftwareCard
            key={genericItem.id}
            image={{ imageId: genericItem.imageId, alt: genericItem.title }}
            title={genericItem.title}
            link={itemLink}
            rating={genericItem.userRating / 10}
            countRating={genericItem.userRatingNb}
            countDownloads={genericItem.popularity}
            {...genericItem}
          />
        );
      }) ?? null
    );
  }, [data.softwareSimilar?.genericItemList]);

  const screenshots = data?.screenshots?.medias;
  const dropdownLinks = useMemo(
    () => [
      {
        title: getTitle(links[0]),
        onClick: () => {
          window.scrollTo(0, 0);
          triggerModal(fileOsDefault);
        },
      },
    ],
    [fileOsDefault, getTitle, links, triggerModal],
  );

  return (
    <>
      <Ad className="Billboard_1" desktopOnly megaban />
      <Breadcrumb breadcrumb={breadcrumb} isFullWidth />
      <SpacedContainer>
        <MainContent>
          <DownloadHeader>
            <DownloadHeaderLeft>
              <DownloadHeaderImage>
                <Image
                  imageId={item.imageId}
                  mobileWidth={45}
                  mobileHeight={45}
                  desktopWidth={94}
                  desktopHeight={94}
                  alt={item.title}
                />
              </DownloadHeaderImage>
              <DownloadHeaderUpdate>
                {t('sdk.template.download.update')} :{' '}
                <DateDisplay dateTime={item.updatedAt} pattern={t('localeTimeSoftWare')} />
              </DownloadHeaderUpdate>
            </DownloadHeaderLeft>
            <DownloadHeaderRight>
              <DownloadHeaderHeading>
                <DownloadHeaderImage className="mod-mobile">
                  <Image
                    imageId={item.imageId}
                    mobileWidth={45}
                    mobileHeight={45}
                    desktopWidth={94}
                    desktopHeight={94}
                    alt={item.title}
                  />
                </DownloadHeaderImage>
                <HeadingOne className="mod-x-smaller mod-no-margin">{item.title}</HeadingOne>
              </DownloadHeaderHeading>
              <MobileDownloadHeaderUpdate>
                {t('sdk.template.download.update')} :{' '}
                <DateDisplay dateTime={item.updatedAt} pattern={t('localeTimeSoftWare')} />
              </MobileDownloadHeaderUpdate>
              <Paragraph>{item.lead}</Paragraph>
              <DownloadHeaderStats id="download-stats">
                <DownloadHeaderStars>
                  <DownloadHeaderRating>
                    <Stars max={5} rating={item?.userRating / 20 ?? 0} />
                    <RatingValue>
                      {item?.userRating / 10 ?? 0}
                      <RatingValueComp>/10</RatingValueComp>
                    </RatingValue>
                  </DownloadHeaderRating>
                  <DownloadHeaderStatsMark>
                    ({item?.userRatingNb ?? 0} {t('sdk.template.download.marks')})
                  </DownloadHeaderStatsMark>
                </DownloadHeaderStars>
                <DownloadHeaderHr />
                <DownloadAdvise>
                  <DownloadAdviseRating>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <DownloadAdviseRatingStar
                        key={i}
                        onMouseMove={(event: any) => ratingHover(event, i)}
                        onClick={() => setValidateRating(true)}
                      >
                        <StarsRate i={i} rating={rating} />
                      </DownloadAdviseRatingStar>
                    ))}
                  </DownloadAdviseRating>
                  <DownloadAdviseLabel>{t('sdk.template.download.commend_download')}</DownloadAdviseLabel>
                </DownloadAdvise>
              </DownloadHeaderStats>
            </DownloadHeaderRight>
          </DownloadHeader>

          {validateRating && (
            <DownloadRatingMessageValidate onClick={handleLike}>
              {t('sdk.template.download.validate_rate')}
            </DownloadRatingMessageValidate>
          )}

          {message && !validateRating && <DownloadRatingMessage>{message}</DownloadRatingMessage>}

          <ViewerProvider>
            {mainContent}
            <SoftwareSpecs
              allFiles={allFiles}
              currentFileId={fileOsDefault.id}
              softwareStatistics={item.popularity}
              updateAt={item.updatedAt}
            />
            {screenshots && screenshots.length > 0 ? <SoftwareScreenshots screenshots={screenshots} /> : null}
          </ViewerProvider>

          <ButtonDropdown links={dropdownLinks} isRotate>
            {fileOsDefault.os !== 'Web' && <Disk width={15} height={18} color="var(--theme-palette-white)" />}
          </ButtonDropdown>

          {links.length > 1 && (
            <AlsoAvailable onMouseEnter={() => setIsModal(adContext.isModal)}>
              <HeadingTwo>{t('sdk.template.download.alsoAvailable', { title: item.title })}</HeadingTwo>
              <OtherFiles software={item.title} files={links} />
            </AlsoAvailable>
          )}
        </MainContent>
        <SideContent>
          <SideContentHalf>
            <SideContentSticky>
              {dataZoneSponso?.isEmpty ? null : (
                <>
                  <SponsoFactory zone={dataZoneSponso as DataZone<ComponentProps<DataType>, TemplateType>} />
                  <SideContentSpacer />
                </>
              )}
              <Ad className="HalfpageAd_1" desktopOnly />
              {data.topDownload?.data ? (
                <SideContentSpacer>
                  <SideContentTopDownloadTitle>{data.topDownload.data.info.title}</SideContentTopDownloadTitle>
                  <ListItemRow>
                    {data.topDownload.data?.products?.productVariantList?.map((product: CustomeGenericItem) => {
                      const imageProps = {
                        imageId: product.imageId,
                        alt: product.title,
                      };

                      const LinkProps = {
                        href: product.urlCta ?? product.url,
                        text: t('sdk.template.download.download'),
                      };

                      return (
                        <ItemCtaSimple
                          small={false}
                          callback={clickTopApp}
                          key={product.id}
                          href={product.url}
                          title={product.title}
                          image={imageProps}
                          cta={LinkProps}
                          subtitle={product.title}
                        />
                      );
                    })}
                  </ListItemRow>
                </SideContentSpacer>
              ) : null}
            </SideContentSticky>
          </SideContentHalf>
          <SideContentHalf>
            <SideContentSticky>
              <SideContentSpacer>
                <Ad className="HalfpageAd_2" desktopOnly />
              </SideContentSpacer>
            </SideContentSticky>
          </SideContentHalf>
        </SideContent>
      </SpacedContainer>
      {data.softwareSimilar?.genericItemList?.length && (
        <Container>
          <FullContentRelative className="mod-spacer-top">
            <RelativeTitle className="mod-fat">{t('sdk.template.download.similar_downloads')}</RelativeTitle>
            <Slider items={alternateSoftwares} />
            <Background />
          </FullContentRelative>
        </Container>
      )}
      {modalProps && currentFile?.id ? (
        <Modal
          {...modalProps}
          campaignName={campaignName}
          os={fileOsDefault.os}
          userAgentOs={userAgentOs}
          software={item.title}
          callback={handlerPreLaunchedDownload}
          fileId={currentFile?.id}
          trigger={trigger}
          clickRef={modalData?.clickRef}
          url={metaSeoContext.url}
          trackEvent={trackEvent}
        />
      ) : null}
    </>
  );
};

export default DownloadItem;
